/**
 * Resolve a stored Firebase Storage path to a fetch-able URL.
 *
 * Firebase Storage rules on this bucket grant `read: if true` for every
 * path — so the `firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media`
 * URL is already publicly accessible without auth, without a token, and
 * without round-tripping through the Storage SDK's `getDownloadURL`.
 *
 * We used to call `getDownloadURL` once per tour, which (a) added ~50ms
 * each, (b) needed a concurrency cap of 6 to stop the browser from
 * blowing through its socket budget on dashboards with 50+ tours, and
 * (c) made dashboard tour-card population feel sluggish because the
 * Promise.all in getAllToursFast blocked on the slowest batch. Building
 * the URL synchronously here makes the resolution effectively free.
 */

const STORAGE_BUCKET =
  process.env.REACT_APP_STORAGE_BUCKET || 'id3d-prod.appspot.com';

function isResolvablePath(path: unknown): path is string {
  // Legacy v1 tour docs sometimes store the literal string "not set" or
  // an empty string in thumbnailUrl. Reject anything that isn't a real
  // storage object path — they'd just 404 and clutter the network tab.
  if (typeof path !== 'string') return false;
  const trimmed = path.trim();
  if (!trimmed) return false;
  if (trimmed === 'not set') return false;
  // A genuine storage path always has a slash (thumbnails/...,
  // tours/.../thumbnail/..., etc.).
  return trimmed.includes('/');
}

export async function getThumbnailUrlFromStorage(
  path: string,
): Promise<string> {
  if (!isResolvablePath(path)) {
    throw new Error(`Unresolvable thumbnail path: ${JSON.stringify(path)}`);
  }
  return `https://firebasestorage.googleapis.com/v0/b/${STORAGE_BUCKET}/o/${encodeURIComponent(
    path,
  )}?alt=media`;
}
